/**
 * Charts Tab Component
 * Displays visual trends for Income Statement and Balance Sheet data
 */
import React from 'react';
import PropTypes from 'prop-types';
import { TrendingUp, BarChart3 } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const ChartsTab = ({ years, financialData, formatCurrency }) => {
  // Prepare chart data for Income Statement
  const chartData = years.map(year => ({
    year: year.toString(),
    Revenue: parseFloat(financialData.revenue?.[year] || 0),
    'Gross Profit': parseFloat(financialData.gross_profit?.[year] || 0),
    EBITDA: parseFloat(financialData.ebitda?.[year] || 0),
    SDE: parseFloat(financialData.sde?.[year] || 0)
  }));

  // Prepare chart data for Balance Sheet
  const balanceSheetData = years.map(year => ({
    year: year.toString(),
    'Total Assets': parseFloat(financialData.total_assets?.[year] || 0),
    'Total Liabilities': parseFloat(financialData.total_liabilities?.[year] || 0),
    'Total Equity': parseFloat(financialData.equity?.[year] || 0)
  }));

  return (
    <div>
      {/* Combined Income Statement Chart */}
      <div className="mb-6 sm:mb-8 bg-white border rounded-lg p-3 sm:p-6">
        <h4 className="text-sm sm:text-md font-semibold mb-3 sm:mb-4 flex items-center gap-2">
          <TrendingUp className="text-blue-600" size={18} />
          Income Statement Trends
        </h4>
        <ResponsiveContainer width="100%" height={300} className="sm:h-350">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`} tick={{ fontSize: 11 }} width={50} />
            <Tooltip
              formatter={(v) => formatCurrency(v)}
              itemSorter={(item) => {
                const order = ['Revenue', 'Gross Profit', 'EBITDA', 'SDE'];
                return order.indexOf(item.name);
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '12px' }}
              content={(props) => {
                const items = [
                  { value: 'Revenue', color: '#f97316' },
                  { value: 'Gross Profit', color: '#10b981' },
                  { value: 'EBITDA', color: '#3b82f6' },
                  { value: 'SDE', color: '#9333ea' }
                ];
                return (
                  <ul style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', listStyle: 'none', padding: 0, margin: 0, gap: '8px' }}>
                    {items.map((item, index) => (
                      <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <svg width="16" height="10" style={{ display: 'inline-block' }}>
                          <line x1="0" y1="5" x2="16" y2="5" stroke={item.color} strokeWidth="2" />
                        </svg>
                        <span style={{ fontSize: '11px' }}>{item.value}</span>
                      </li>
                    ))}
                  </ul>
                );
              }}
            />
            <Line type="monotone" dataKey="Revenue" stroke="#f97316" strokeWidth={2} />
            <Line type="monotone" dataKey="Gross Profit" stroke="#10b981" strokeWidth={2} />
            <Line type="monotone" dataKey="EBITDA" stroke="#3b82f6" strokeWidth={2} />
            <Line type="monotone" dataKey="SDE" stroke="#9333ea" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Balance Sheet Chart */}
      <div className="mb-6 sm:mb-8 bg-white border rounded-lg p-3 sm:p-6">
        <h4 className="text-sm sm:text-md font-semibold mb-3 sm:mb-4 flex items-center gap-2">
          <BarChart3 className="text-green-600" size={18} />
          Balance Sheet Trends
        </h4>
        <ResponsiveContainer width="100%" height={300} className="sm:h-350">
          <LineChart data={balanceSheetData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`} tick={{ fontSize: 11 }} width={50} />
            <Tooltip
              formatter={(v) => formatCurrency(v)}
              itemSorter={(item) => {
                const order = ['Total Assets', 'Total Liabilities', 'Total Equity'];
                return order.indexOf(item.name);
              }}
            />
            <Legend
              wrapperStyle={{ paddingTop: '12px' }}
              content={(props) => {
                const items = [
                  { value: 'Total Assets', color: '#3b82f6' },
                  { value: 'Total Liabilities', color: '#ef4444' },
                  { value: 'Total Equity', color: '#10b981' }
                ];
                return (
                  <ul style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', listStyle: 'none', padding: 0, margin: 0, gap: '8px' }}>
                    {items.map((item, index) => (
                      <li key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <svg width="16" height="10" style={{ display: 'inline-block' }}>
                          <line x1="0" y1="5" x2="16" y2="5" stroke={item.color} strokeWidth="2" />
                        </svg>
                        <span style={{ fontSize: '11px' }}>{item.value}</span>
                      </li>
                    ))}
                  </ul>
                );
              }}
            />
            <Line type="monotone" dataKey="Total Assets" stroke="#3b82f6" strokeWidth={2} />
            <Line type="monotone" dataKey="Total Liabilities" stroke="#ef4444" strokeWidth={2} />
            <Line type="monotone" dataKey="Total Equity" stroke="#10b981" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

ChartsTab.propTypes = {
  years: PropTypes.arrayOf(PropTypes.number).isRequired,
  financialData: PropTypes.object.isRequired,
  formatCurrency: PropTypes.func.isRequired
};

export default ChartsTab;
